import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { Fragment } from "react";

import { ACCENT_DEEP, marketingPalette } from "@/components/marketing/tokens";
import type { LandingCta } from "@/content/landing/types";
import { BoldRuns } from "../LandingPrimitives";

type Links = ReadonlyArray<LandingCta> | undefined;

/**
 * One published paragraph, however many lines it prints. The published copy
 * breaks a paragraph with `<br>` rather than closing it, so a newline in the
 * registry's string is that break and not a paragraph of its own: two
 * paragraphs would carry the column's 16px gutter between them, and the
 * published break carries none. A run the CMS sets in bold is written `**…**`.
 */
export function RichParagraph({
  text,
  links,
  sx,
}: {
  text: string;
  links?: Links;
  sx: SxProps<Theme>;
}) {
  return (
    <Typography sx={sx}>
      <RichRuns text={text} links={links} />
    </Typography>
  );
}

/** The lines of `RichParagraph` without its element, for a holder that is one already. */
export function RichRuns({ text, links }: { text: string; links?: Links }) {
  return (
    <>
      {text.split("\n").map((line, index) => (
        <Fragment key={index}>
          {index > 0 ? <br /> : null}
          <BoldRuns text={line} links={links} />
        </Fragment>
      ))}
    </>
  );
}

/** `.compliance-bullet_item`: the dot the alternative pages list points with. */
const DOT_ROW = {
  display: "flex",
  alignItems: "flex-start",
  gap: "10.08px",
  color: marketingPalette.ink,
  fontSize: 15,
  lineHeight: "22.5px",
} satisfies SxProps<Theme>;

const DOT = {
  flex: "0 0 auto",
  width: "7.5px",
  height: "7.5px",
  mt: "8.48px",
  borderRadius: "100vw",
  backgroundColor: ACCENT_DEEP,
} satisfies SxProps<Theme>;

/**
 * `.compliance-bullet_list`: a list of dotted lines, no wider than its longest
 * one, which is what the published column's `flex-start` leaves it at.
 */
export function DotList({ items }: { items: ReadonlyArray<string> }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      {items.map((item, index) => (
        <Box key={index} sx={DOT_ROW}>
          <Box sx={DOT} />
          <Box>{item}</Box>
        </Box>
      ))}
    </Box>
  );
}
